/**
 * Centralised formatters — date/distance/speed/etc.
 * Use these everywhere so unit & locale switches are single-line changes.
 */

/** ISO-style UTC formatter matching the mockup label format (L4920):
 *  `YYYY-MM-DDTHH:MM UTC` (e.g. `2026-03-27T16:00 UTC`). Same shape
 *  everywhere so OI Schedule / Updated / Submitted lines read uniformly
 *  regardless of how far in the past or future the value is. */
export function formatUtc(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const yyyy = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${yyyy}-${mo}-${dd}T${hh}:${mm} UTC`;
}

export function formatDuration(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '—';
  const m = Math.max(0, Math.floor(ms / 60_000));
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

export function formatAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

export function formatDistanceKm(km: number | null | undefined): string {
  if (km == null) return '—';
  return `${km.toFixed(2)} km`;
}

export function formatDistanceNm(nm: number | null | undefined): string {
  if (nm == null) return '—';
  return `${nm.toFixed(1)} nm`;
}

export function formatFt(ft: number | null | undefined): string {
  if (ft == null) return '—';
  return `${Math.round(ft).toLocaleString()} ft`;
}

export function formatMeters(m: number | null | undefined): string {
  if (m == null) return '—';
  return `${Math.round(m)} m`;
}

export function formatLatLng(lat: number | null | undefined, lng: number | null | undefined): string {
  if (lat == null || lng == null) return '—';
  return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
}

const CARDINALS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
export function degToCardinal(deg: number | null | undefined): string {
  if (deg == null) return '—';
  return CARDINALS[Math.round(((deg + 360) % 360) / 45) % 8] ?? '—';
}

/* ---- Telemetry display helpers (ENG-232) -------------------------------- */

export const FT_PER_M = 3.28084;

export function metresToFeet(m: number | null | undefined): number | null {
  return m == null ? null : m * FT_PER_M;
}

/** DJI publishes heading as -180..180; MAVLink as 0..360. */
export function normaliseHeading(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/** `237° SW` — normalised, with the cardinal point. */
export function formatHeading(deg: number | null | undefined): string {
  if (deg == null || !Number.isFinite(deg)) return '—';
  const h = normaliseHeading(deg);
  return `${Math.round(h) % 360}° ${degToCardinal(h)}`;
}

/** Ground speed, m/s. */
export function formatSpeedMps(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v.toFixed(1)} m/s`;
}

/** Vertical speed, m/s, signed (+ climbing, − descending). */
export function formatVerticalSpeedMps(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const r = Math.round(v * 10) / 10;
  return `${r > 0 ? '+' : ''}${r.toFixed(1)} m/s`;
}

/** Whole-number percentage. MAVLink publishes a float, DJI an int. */
export function formatPercent(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${Math.round(v)}%`;
}

/** Position / speed accuracy, `±0.3 m`. */
export function formatAccuracyM(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `±${Number.isInteger(v) ? v : v.toFixed(1)} m`;
}

export function formatDbm(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${Math.round(v)} dBm`;
}

/** Speed in knots, whole numbers: feeds send values like 219.5723953504. */
export function formatKnots(kts: number | null | undefined): string {
  return kts == null || !Number.isFinite(kts) ? '—' : `${Math.round(kts)} kts`;
}
